import React, { useContext, useState, useMemo, useCallback, useEffect, useRef } from "react";
import { FilterContext, ClientsContext, ClientGroupsContext, DataStateContext } from "@/App";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { ChevronRight, ChevronDown, X, Search, SlidersHorizontal, Check, Minus } from "lucide-react";

function nodeState(names, selected) {
  if (!names || names.length === 0) return "unchecked";
  if (!selected || selected.length === 0) return "unchecked";
  const hits = names.filter(n => selected.includes(n)).length;
  if (hits === 0) return "unchecked";
  if (hits === names.length) return "checked";
  return "indeterminate";
}

function TriBox({ state }) {
  const filled = state === "checked" || state === "indeterminate";
  return (
    <span className={`h-4 w-4 rounded-[4px] border flex items-center justify-center shrink-0 ${filled ? "bg-orange-600 border-orange-600" : "border-[hsl(var(--border))] bg-transparent"}`}>
      {state === "checked" && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
      {state === "indeterminate" && <Minus className="w-3 h-3 text-white" strokeWidth={3} />}
    </span>
  );
}

function Logo({ svg, color, size = 16 }) {
  if (svg) {
    return (
      <span className="inline-flex items-center justify-center shrink-0 [&>svg]:w-full [&>svg]:h-full" style={{ width: size, height: size }} dangerouslySetInnerHTML={{ __html: svg }} />
    );
  }
  return <span className="rounded-full shrink-0" style={{ width: size * 0.55, height: size * 0.55, backgroundColor: color || "#94a3b8", display: "inline-block" }} />;
}

export function resolveClientFilterChips(selected, clients, clientGroups) {
  if (!selected || selected.length === 0) return [];
  const nameById = id => (clients || []).find(c => c.id === id)?.name;
  const chips = [];
  const covered = new Set();
  (clientGroups || []).filter(g => g.type === "group").forEach(group => {
    const subs = (clientGroups || []).filter(sg => sg.type === "subgroup" && sg.parent_id === group.id);
    const groupNames = [
      ...(group.client_ids || []).map(nameById).filter(Boolean),
      ...subs.flatMap(sg => (sg.client_ids || []).map(nameById).filter(Boolean)),
    ];
    if (groupNames.length > 0 && groupNames.every(n => selected.includes(n))) {
      chips.push({ label: group.name, color: group.color || "#94a3b8", names: groupNames });
      groupNames.forEach(n => covered.add(n));
    } else {
      subs.forEach(sg => {
        const sgNames = (sg.client_ids || []).map(nameById).filter(Boolean);
        if (sgNames.length > 0 && sgNames.every(n => selected.includes(n))) {
          chips.push({ label: sg.name, color: sg.color || "#94a3b8", names: sgNames });
          sgNames.forEach(n => covered.add(n));
        }
      });
    }
  });
  selected.forEach(name => {
    if (!covered.has(name)) {
      const c = (clients || []).find(cl => cl.name === name);
      chips.push({ label: name, color: c?.color || "#94a3b8", names: [name] });
    }
  });
  return chips;
}

export function GlobalFilterContent() {
  const { globalFilter, setGlobalFilter } = useContext(FilterContext);
  const { clients } = useContext(ClientsContext);
  const { clientGroups } = useContext(ClientGroupsContext);
  const { items } = useContext(DataStateContext);

  const [searchQ, setSearchQ] = useState("");
  const [expanded, setExpanded] = useState({});
  const [draft, setDraft] = useState(globalFilter.clients);
  const [applying, setApplying] = useState(false);
  const commitTimer = useRef(null);

  useEffect(() => { setDraft(globalFilter.clients); }, [globalFilter.clients]);

  useEffect(() => {
    if (commitTimer.current) clearTimeout(commitTimer.current);
    if (JSON.stringify(draft) === JSON.stringify(globalFilter.clients)) { setApplying(false); return; }
    setApplying(true);
    commitTimer.current = setTimeout(() => {
      setGlobalFilter(f => ({ ...f, clients: draft }));
      setApplying(false);
    }, 1000);
    return () => clearTimeout(commitTimer.current);
  }, [draft, setGlobalFilter, globalFilter.clients]);

  const totalClients = (clients || []).length;

  const valueByClient = useMemo(() => {
    const map = {};
    (items || []).forEach(item => { if (item.client) map[item.client] = (map[item.client] || 0) + (item.value_usd || 0); });
    return map;
  }, [items]);

  const tree = useMemo(() => {
    const val = name => valueByClient[name] || 0;
    const byClientVal = (a, b) => val(b.name) - val(a.name) || a.name.localeCompare(b.name);
    const sumNames = names => names.reduce((s, n) => s + val(n), 0);
    const resolve = ids => (ids || []).map(id => (clients || []).find(c => c.id === id)).filter(Boolean).sort(byClientVal);
    const allClientIds = new Set((clientGroups || []).flatMap(g => g.client_ids || []));
    const topGroups = (clientGroups || [])
      .filter(g => g.type === "group")
      .map(group => {
        const subgroups = (clientGroups || [])
          .filter(sg => sg.type === "subgroup" && sg.parent_id === group.id)
          .map(sg => ({ ...sg, clientItems: resolve(sg.client_ids) }))
          .sort((a, b) => sumNames(b.clientItems.map(c => c.name)) - sumNames(a.clientItems.map(c => c.name)));
        const directClientItems = resolve(group.client_ids);
        const groupValue = sumNames([...directClientItems.map(c => c.name), ...subgroups.flatMap(sg => sg.clientItems.map(c => c.name))]);
        return { ...group, subgroups, directClientItems, groupValue };
      })
      .sort((a, b) => b.groupValue - a.groupValue);
    const ungroupedClients = (clients || []).filter(c => !allClientIds.has(c.id)).sort(byClientVal);
    return { topGroups, ungroupedClients };
  }, [clients, clientGroups, valueByClient]);

  const isSel = useCallback(name => draft != null && draft.includes(name), [draft]);

  const toggleNames = useCallback((names) => {
    setDraft(cur => {
      const prev = cur || [];
      const allIn = names.every(n => prev.includes(n));
      const next = allIn ? prev.filter(n => !names.includes(n)) : [...prev, ...names.filter(n => !prev.includes(n))];
      return next.length === 0 ? null : next;
    });
  }, []);

  const toggleClient = useCallback(name => toggleNames([name]), [toggleNames]);
  const toggleExpand = useCallback(id => setExpanded(e => ({ ...e, [id]: e[id] === false ? true : false })), []);

  const groupDescNames = useCallback(group => [
    ...(group.directClientItems || []).map(c => c.name),
    ...(group.subgroups || []).flatMap(sg => (sg.clientItems || []).map(c => c.name)),
  ], []);
  const subDescNames = useCallback(sg => (sg.clientItems || []).map(c => c.name), []);

  const q = searchQ.toLowerCase();
  const matchStr = name => !q || name.toLowerCase().includes(q);
  const groupMatches = group => matchStr(group.name) || (group.directClientItems || []).some(c => matchStr(c.name)) || (group.subgroups || []).some(sg => matchStr(sg.name) || (sg.clientItems || []).some(c => matchStr(c.name)));
  const subMatches = sg => matchStr(sg.name) || (sg.clientItems || []).some(c => matchStr(c.name));

  const activeChips = useMemo(() => resolveClientFilterChips(draft, clients, clientGroups), [draft, clients, clientGroups]);

  const clientRow = (c, parentLogoSvg) => {
    const cSel = isSel(c.name);
    const logoSvg = c.logo_svg || parentLogoSvg || null;
    return (
      <div key={c.id} role="button" tabIndex={0}
        onClick={() => toggleClient(c.name)}
        onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleClient(c.name); } }}
        className={`flex items-center gap-2 w-full rounded-md cursor-pointer select-none transition-colors min-h-[30px] px-2 outline-none focus-visible:ring-2 focus-visible:ring-orange-500 hover:bg-[hsl(var(--muted))]/60 ${cSel ? "bg-orange-500/10" : ""}`}
        style={cSel ? { boxShadow: "inset 3px 0 0 #f97316" } : {}}
        data-testid={`gf-client-${c.name}`}>
        <span className="w-4 shrink-0" />
        <TriBox state={cSel ? "checked" : "unchecked"} />
        <Logo svg={logoSvg} color={c.color} size={14} />
        <span className={`text-xs flex-1 truncate ${cSel ? "text-orange-600" : "text-[hsl(var(--foreground))]"}`}>{c.name}</span>
      </div>
    );
  };

  return (
    <div className="flex flex-col min-h-[440px] relative" data-testid="global-filter-content">
      {applying && (
        <div className="absolute top-0 inset-x-0 h-[3px] overflow-hidden rounded-t-xl z-10">
          <div className="rl-loading-bar h-full w-full" />
        </div>
      )}

      <div className="flex items-center justify-between pb-2.5 border-b border-[hsl(var(--border))]/60 mb-2">
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="w-4 h-4 text-orange-500" />
          <span className="text-sm font-bold text-[hsl(var(--foreground))]">Global Filters</span>
        </div>
        {draft && draft.length > 0 && (
          <button onClick={() => { setDraft(null); setGlobalFilter(f => ({ ...f, clients: null })); if (commitTimer.current) clearTimeout(commitTimer.current); setApplying(false); }}
            className="text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] underline underline-offset-2 transition-colors"
            data-testid="clear-all-filters">Clear clients</button>
        )}
      </div>

      <div className="relative mb-1.5">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[hsl(var(--muted-foreground))]" />
        <input type="text" value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Search groups or clients…"
          className="w-full h-7 pl-8 pr-3 text-xs rounded-md bg-[hsl(var(--muted))]/60 border border-[hsl(var(--border))] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))]/60 focus:outline-none focus:ring-2 focus:ring-orange-500" />
      </div>

      <div className="flex-1 overflow-y-auto max-h-[360px] pr-0.5 mb-2">
        {tree.topGroups.filter(groupMatches).map(group => {
          const allNames = groupDescNames(group);
          const state = nodeState(allNames, draft);
          const isOpen = (expanded[group.id] !== false) || (!!q && groupMatches(group));
          const isChecked = state === "checked";
          return (
            <div key={group.id} data-testid={`gf-group-${group.id}`}>
              <div role="button" tabIndex={0} onClick={() => toggleNames(allNames)}
                onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleNames(allNames); } }}
                className={`flex items-center gap-2 w-full rounded-md cursor-pointer select-none transition-colors min-h-[34px] px-2 outline-none focus-visible:ring-2 focus-visible:ring-orange-500 hover:bg-[hsl(var(--muted))]/60 ${isChecked ? "bg-orange-500/10" : ""}`}
                style={isChecked ? { boxShadow: "inset 3px 0 0 #f97316" } : {}}>
                <button onClick={e => { e.stopPropagation(); toggleExpand(group.id); }} className="w-4 h-4 flex items-center justify-center shrink-0 text-[hsl(var(--muted-foreground))]" tabIndex={-1}>
                  {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                </button>
                <TriBox state={state} />
                <Logo svg={group.logo_svg} color={group.color} size={18} />
                <span className={`text-xs font-semibold flex-1 truncate ${isChecked ? "text-orange-600" : "text-[hsl(var(--foreground))]"}`}>{group.name}</span>
                <span className="text-[10px] text-[hsl(var(--muted-foreground))] shrink-0">{allNames.length}</span>
              </div>
              {isOpen && (
                <div className="pl-3">
                  {(group.directClientItems || []).filter(c => matchStr(c.name)).map(c => clientRow(c, group.logo_svg))}
                  {(group.subgroups || []).filter(subMatches).map(sg => {
                    const sgNames = subDescNames(sg);
                    const sgState = nodeState(sgNames, draft);
                    const sgChecked = sgState === "checked";
                    const sgOpen = (expanded[sg.id] !== false) || (!!q && subMatches(sg));
                    return (
                      <div key={sg.id} data-testid={`gf-group-${sg.id}`}>
                        <div role="button" tabIndex={0} onClick={() => toggleNames(sgNames)}
                          onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleNames(sgNames); } }}
                          className={`flex items-center gap-2 w-full rounded-md cursor-pointer select-none transition-colors min-h-[34px] px-2 outline-none focus-visible:ring-2 focus-visible:ring-orange-500 hover:bg-[hsl(var(--muted))]/60 ${sgChecked ? "bg-orange-500/10" : ""}`}
                          style={sgChecked ? { boxShadow: "inset 3px 0 0 #f97316" } : {}}>
                          <button onClick={e => { e.stopPropagation(); toggleExpand(sg.id); }} className="w-4 h-4 flex items-center justify-center shrink-0 text-[hsl(var(--muted-foreground))]" tabIndex={-1}>
                            {sgOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                          </button>
                          <TriBox state={sgState} />
                          <Logo svg={sg.logo_svg || group.logo_svg} color={sg.color} size={16} />
                          <span className={`text-xs font-medium flex-1 truncate ${sgChecked ? "text-orange-600" : "text-[hsl(var(--foreground))]"}`}>{sg.name}</span>
                          <span className="text-[10px] text-[hsl(var(--muted-foreground))] shrink-0">{sgNames.length}</span>
                        </div>
                        {sgOpen && (
                          <div className="pl-3">
                            {(sg.clientItems || []).filter(c => matchStr(c.name)).map(c => clientRow(c, sg.logo_svg || group.logo_svg))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {tree.ungroupedClients.filter(c => matchStr(c.name)).length > 0 && (() => {
          const ugNames = tree.ungroupedClients.map(c => c.name);
          const ugState = nodeState(ugNames, draft);
          const ugChecked = ugState === "checked";
          const ugOpen = (expanded["__ungrouped__"] !== false) || !!q;
          return (
            <div>
              <div role="button" tabIndex={0} onClick={() => toggleNames(ugNames)}
                onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleNames(ugNames); } }}
                className={`flex items-center gap-2 w-full rounded-md cursor-pointer select-none transition-colors min-h-[34px] px-2 outline-none focus-visible:ring-2 focus-visible:ring-orange-500 hover:bg-[hsl(var(--muted))]/60 ${ugChecked ? "bg-orange-500/10" : ""}`}
                style={ugChecked ? { boxShadow: "inset 3px 0 0 #f97316" } : {}}>
                <button onClick={e => { e.stopPropagation(); toggleExpand("__ungrouped__"); }} className="w-4 h-4 flex items-center justify-center shrink-0 text-[hsl(var(--muted-foreground))]" tabIndex={-1}>
                  {ugOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                </button>
                <TriBox state={ugState} />
                <span className={`text-xs font-semibold flex-1 truncate ${ugChecked ? "text-orange-600" : "text-[hsl(var(--muted-foreground))]"}`}>Ungrouped</span>
                <span className="text-[10px] text-[hsl(var(--muted-foreground))] shrink-0">{tree.ungroupedClients.length}</span>
              </div>
              {ugOpen && (
                <div className="pl-3">
                  {tree.ungroupedClients.filter(c => matchStr(c.name)).map(c => clientRow(c, null))}
                </div>
              )}
            </div>
          );
        })()}
      </div>

      <div className="flex items-center gap-2 pb-2 border-b border-[hsl(var(--border))]/40">
        <span className="text-[10px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))] shrink-0">Priority</span>
        {[
          { val: "all", label: "All", activeStyle: { background: "rgba(99,102,241,0.2)", borderColor: "rgba(99,102,241,0.6)", color: "#fb923c" } },
          { val: "Standard", label: "Standard", activeStyle: { background: "rgba(99,102,241,0.2)", borderColor: "rgba(99,102,241,0.6)", color: "#fb923c" } },
          { val: "Expedite", label: "Expedite", activeStyle: { background: "rgba(249,115,22,0.15)", borderColor: "rgba(249,115,22,0.5)", color: "#f97316" } },
        ].map(({ val, label, activeStyle }) => {
          const active = globalFilter.priority === val;
          return (
            <button key={val} onClick={() => setGlobalFilter(f => ({ ...f, priority: val }))}
              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${active ? "" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]"}`}
              style={active ? activeStyle : {}}>{label}</button>
          );
        })}
      </div>

      <div className="min-h-[44px] pt-1.5 flex flex-col gap-1">
        <div className="flex flex-wrap gap-1 flex-1">
          {activeChips.map(chip => (
            <span key={chip.label} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border"
              style={{ color: chip.color, backgroundColor: chip.color + "20", borderColor: chip.color + "50" }}>
              {chip.label}
              <button onClick={() => toggleNames(chip.names)} className="hover:opacity-70 ml-0.5" aria-label={`Remove ${chip.label}`}>
                <X className="w-2.5 h-2.5" />
              </button>
            </span>
          ))}
        </div>
        <div className="text-[10px] text-[hsl(var(--muted-foreground))] text-right">
          {draft?.length || 0} of {totalClients} clients selected
        </div>
      </div>
    </div>
  );
}

export default function GlobalFilterPanel({ open, onClose }) {
  return (
    <Sheet open={open} onOpenChange={o => { if (!o) onClose(); }}>
      <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto px-4 pb-6 pt-4 bg-[hsl(var(--card))] border-[hsl(var(--border))]" data-testid="global-filter-panel">
        <GlobalFilterContent />
      </SheetContent>
    </Sheet>
  );
}
